/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _parseInt from "@babel/runtime-corejs3/core-js-stable/parse-int";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _mapInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/map";

/** @module react/plugins/input-validators/date-validator */
import { dateUtils, numberUtils, regExpUtils } from '@codistica/core';
import { Types } from '@codistica/types';
import { InputValidatorPluginUtils } from '../../../classes/input-validator-plugin-utils.js';
var dateValidatorSchema = new Types({
  options: {
    type: 'Object',
    def: {
      minDate: {
        type: 'Object',
        def: null
      },
      maxDate: {
        type: 'Object',
        def: null
      },
      minAge: {
        type: 'number',
        min: 0,
        max: Infinity,
        def: null
      },
      format: {
        type: 'Array<number>',
        def: [31, 12, 9999]
      },
      separator: {
        type: 'string',
        def: null
      },
      errorMessages: {
        type: 'Object',
        def: {
          generic: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          minDate: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          maxDate: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          minAge: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          format: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          separator: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          }
        }
      }
    }
  }
});
/**
 * @typedef dateValidatorErrorMessagesType
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [generic=null] - Generic error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [minDate=null] - Minimum required date error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [maxDate=null] - Maximum acceptable date error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [minAge=null] - Minimum required age error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [format=null] - Enforced format error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [separator=null] - Enforced separator error message.
 */

/**
 * @typedef dateValidatorOptionsType
 * @property {(Date|null)} [minDate=null] - Minimum required date.
 * @property {(Date|null)} [maxDate=null] - Maximum acceptable date.
 * @property {(number|null)} [minAge=null] - Minimum required age.
 * @property {Array<number>} [format=[31, 12, 9999]] - Enforced format.
 * @property {string} [separator=null] - Enforced separator.
 * @property {dateValidatorErrorMessagesType} [errorMessages] - Validation error messages.
 */

/**
 * @description Validates input date according to validation options.
 * @param {dateValidatorOptionsType} [options] - Validation options.
 * @returns {{type: 'validator', name: string, messages: Array<string>, plugin: function(string): Object<string,*>}} Validator.
 */

function dateValidator(options) {
  var _dateValidatorSchema$ = dateValidatorSchema.validate({
    options: options
  });

  options = _dateValidatorSchema$.options;
  var utils = new InputValidatorPluginUtils({
    keys: ['minDate', 'maxDate', 'minAge', 'format', 'separator']
  });
  return {
    type: 'validator',
    name: 'dateValidator',
    errorMessages: {
      generic: options.errorMessages.generic
    },

    /**
     * @description Validator.
     * @param {string} value - Input value.
     * @returns {Object<string,*>} - Validation object.
     */
    plugin: function plugin(value) {
      var _context,
          _this = this,
          _context2;

      utils.init(value, true);
      utils.setData('interpretation', null);
      var today = new Date();

      var formatOrder = _mapInstanceProperty(_context = options.format).call(_context, _bindInstanceProperty(_context2 = function _context2(val) {
        _newArrowCheck(this, _this);

        // TODO: SORT INSTEAD! (USE ANOTHER SYSTEM?)
        if (val > 31) {
          return 'year';
        } else if (val > 12) {
          return 'date';
        } else {
          return 'month';
        }
      }).call(_context2, this));

      var dateArray = numberUtils.parseIntAll(value) || [];
      var dateElements = {
        date: null,
        month: null,
        year: null
      }; // CHECK FORMAT

      if (dateArray.length !== options.format.length) {
        utils.invalidate('format', options.errorMessages.format);
      } else {
        var _context3;

        _forEachInstanceProperty(formatOrder).call(formatOrder, _bindInstanceProperty(_context3 = function _context3(key, index) {
          _newArrowCheck(this, _this);

          if (dateArray[index] > options.format[index]) {
            utils.invalidate('format', options.errorMessages.format);
          }

          dateElements[key] = _parseInt(dateArray[index]);
        }).call(_context3, this));

        dateElements.year = dateUtils.getValidFullYear(dateElements.year);
        dateElements.month--;
      }

      if (!utils.isValid('format')) {
        utils.disable('minDate');
        utils.disable('maxDate');
        utils.disable('minAge');
        utils.disable('exists');
        utils.disable('separator');
      } else {
        // CHECK SEPARATOR
        if (options.separator !== null) {
          if ((value.match(new RegExp(regExpUtils.escape(options.separator), 'g')) || []).length !== options.format.length - 1 || (value.match(/\D/g) || []).length !== options.format.length - 1) {
            utils.invalidate('separator', options.errorMessages.separator);
          }
        } // PARSE DATE


        var parsedDate = new Date(dateElements.year, dateElements.month, dateElements.date); // CHECK EXISTENCE

        if (parsedDate.getFullYear() !== dateElements.year || parsedDate.getMonth() !== dateElements.month || parsedDate.getDate() !== dateElements.date) {
          utils.invalidate('exists');
          utils.disable('minDate');
          utils.disable('maxDate');
          utils.disable('minAge');
        } else {
          // SAVE INTERPRETATION
          utils.setData('interpretation', parsedDate.toString()); // CHECK MIN DATE

          if (options.minDate !== null && parsedDate < options.minDate) {
            utils.invalidate('minDate', options.errorMessages.minDate);
          } // CHECK MAX DATE


          if (options.maxDate !== null && parsedDate > options.maxDate) {
            utils.invalidate('maxDate', options.errorMessages.maxDate);
          } // CHECK MIN AGE


          if (options.minAge !== null && new Date(parsedDate.getTime()).setFullYear(parsedDate.getFullYear() + options.minAge) > today) {
            utils.invalidate('minAge', options.errorMessages.minAge);
          }
        }
      }

      return utils.getValidatorOutput();
    }
  };
}

export { dateValidator };